// As peças das telas de entrada (login, convite, senha) no visual do login do PlugChef Desktop
// (plugchef.desktop/src/renderer/src/screens/LoginScreen.tsx): o campo de 52 px com o ícone accent à
// esquerda e a borda accent de 2 px, o "mostrar senha", o botão com o gradiente e a caixa de aviso.
//
// Ficam separadas de components/base.tsx porque só as telas de entrada as usam: dentro do portal os
// campos continuam os do resto do site (`Campo` + `.input`).
import { useId, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react';
import { IconEye, IconEyeOff, IconLock, type IconProps } from './icones';

type Icone = (p: IconProps) => JSX.Element;

type PropsCampo = Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'aria-label' | 'aria-describedby'> & {
  icone: Icone;
  /** O nome do campo: vira o `aria-label` e, sem `placeholder`, o texto de dentro (o Desktop não tem rótulo visível). */
  rotulo: string;
  /** Texto pequeno embaixo do campo (ex.: "Mínimo de 8 caracteres"). */
  dica?: ReactNode;
  /** Classes a mais no <input> (o código do autenticador centraliza e aumenta os números). */
  classeInput?: string;
};

/** O botão principal: 52 px com o gradiente do Desktop. */
export const CLASSE_BOTAO_ENTRADA =
  'flex h-[52px] w-full items-center justify-center rounded-xl bg-gradient-to-r from-[#1f8fd6] to-[#39b6e8] px-4 ' +
  'text-[15px] font-bold text-white transition-[opacity,filter] hover:brightness-110 ' +
  'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-accent/25 ' +
  'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:brightness-100';

/** O botão de segunda opção ("Usar outro e-mail", "Ir para o login"): mesma altura, só a borda. */
export const CLASSE_BOTAO_ENTRADA_SEC =
  'flex h-[52px] w-full items-center justify-center rounded-xl border-2 border-bd bg-transparent px-4 ' +
  'text-[15px] font-semibold text-tx transition-colors hover:border-accent hover:text-accent ' +
  'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-accent/25 ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

/** O link de texto do Desktop ("Esqueci minha senha", "Alterar login"): 13 px, negrito, accent. */
export const CLASSE_LINK_ENTRADA =
  'text-center text-[13px] font-semibold text-accent transition-opacity hover:opacity-80 ' +
  'focus-visible:outline-none focus-visible:underline';

function classeDoInput(somenteLeitura: boolean, comAcao: boolean, extra: string): string {
  return [
    'h-[52px] w-full rounded-xl border-2 bg-inputBg pl-11 text-[15px] outline-none transition-shadow placeholder:text-mut2',
    comAcao ? 'pr-11' : 'pr-4',
    // Só leitura (o e-mail do convite) fica como o usuário já escolhido no Desktop: borda neutra e texto apagado.
    somenteLeitura ? 'cursor-default border-bd text-mut' : 'border-accent text-tx focus:ring-4 focus:ring-accent/15',
    extra,
  ].join(' ');
}

function CampoComIcone({
  icone: Icone, rotulo, dica, classeInput = '', acao, placeholder, readOnly, disabled, ...resto
}: PropsCampo & { acao?: ReactNode }): JSX.Element {
  const idDica = useId();
  const somenteLeitura = Boolean(readOnly || disabled);
  return (
    <div>
      <div className="relative">
        <Icone size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-accent" />
        <input
          {...resto}
          readOnly={readOnly}
          disabled={disabled}
          placeholder={placeholder ?? rotulo}
          aria-label={rotulo}
          aria-describedby={dica ? idDica : undefined}
          className={classeDoInput(somenteLeitura, Boolean(acao), classeInput)}
        />
        {acao}
      </div>
      {dica ? (
        <p id={idDica} className="mt-1.5 px-1 text-[12px] leading-relaxed text-mut2">
          {dica}
        </p>
      ) : null}
    </div>
  );
}

/** Campo de texto da tela de entrada (e-mail, nome, código). */
export function CampoEntrada(props: PropsCampo): JSX.Element {
  return <CampoComIcone {...props} />;
}

/** Campo de senha com o "mostrar senha" do Desktop (o olho à direita). */
export function CampoSenha({
  icone = IconLock, ...props
}: Omit<PropsCampo, 'type' | 'icone'> & { icone?: Icone }): JSX.Element {
  const [visivel, setVisivel] = useState(false);
  const texto = visivel ? 'Ocultar senha' : 'Mostrar senha';
  return (
    <CampoComIcone
      {...props}
      icone={icone}
      type={visivel ? 'text' : 'password'}
      acao={
        // Fora da ordem do Tab, como no Desktop: do e-mail o Tab vai direto à senha e dela ao botão.
        <button
          type="button"
          onClick={() => setVisivel((v) => !v)}
          title={texto}
          aria-label={texto}
          aria-pressed={visivel}
          tabIndex={-1}
          className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-mut transition-colors hover:text-accent"
        >
          {visivel ? <IconEyeOff size={18} /> : <IconEye size={18} />}
        </button>
      }
    />
  );
}

/** O botão principal da tela de entrada (por padrão envia o formulário: aqui o Enter DEVE entrar). */
export function BotaoEntrada({
  type = 'submit', className = '', children, ...resto
}: ButtonHTMLAttributes<HTMLButtonElement>): JSX.Element {
  return (
    <button type={type} {...resto} className={`mt-1 ${CLASSE_BOTAO_ENTRADA} ${className}`}>
      {children}
    </button>
  );
}

/** A caixa de aviso do Desktop: vermelha para o erro, verde para o que deu certo. Sem texto, não aparece. */
export function AvisoEntrada({ tipo = 'erro', children }: { tipo?: 'erro' | 'ok'; children: ReactNode }): JSX.Element | null {
  if (!children) return null;
  const cor = tipo === 'erro' ? 'border-bad/30 bg-bad/10 text-bad' : 'border-good/30 bg-good/10 font-semibold text-good';
  return (
    <div role={tipo === 'erro' ? 'alert' : 'status'} className={`rounded-lg border px-3 py-2 text-[13px] leading-relaxed ${cor}`}>
      {children}
    </div>
  );
}
